<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :actions="actions"
            :refresh.sync="refreshTable"
            :loading="tableIsLoading"   
            ajax-path="pbxware/ring-groups"
            :ajax-params="{tenant_id: $route.meta.serverId}"
            @ajaxResponse="(response) => { extensions = response.extensions }"
        />
        <app-modal
            :show.sync="showEditModal"
            width="md:w-1/2 lg:w-1/4"
            @close="editModalClosed"
        >
            <template slot="header">Edit Ring Group</template>
            <template slot="body">                
                <app-input
                    v-model="editForm.name"
                    placeholder="Group Name"
                    :error="editForm.errors.get('name')"
                    @updated="editForm.errors.clear('name')"
                    focus
                    required
                />
                <app-input
                    v-model="editForm.ext"
                    placeholder="Extension"
                    :error="editForm.errors.get('ext')"
                    @updated="editForm.errors.clear('ext')"
                    required
                />
                <app-input
                    v-model="editForm.extensions"
                    placeholder="Destinations"
                    :error="editForm.errors.get('extensions')"
                    @updated="editForm.errors.clear('extensions')"
                    required
                    help-text="List of all ring group extensions separated by ,"
                />
                <app-input
                    v-model="editForm.max_limit"
                    placeholder="Incoming Limit (per call)"
                    :error="editForm.errors.get('max_limit')"
                    @updated="editForm.errors.clear('max_limit')"
                    :limit="2"
                    integer
                />
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showEditModal = false"
                        :disable="editFormIsLoading"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="update"
                        :loading="editFormIsLoading"
                    >Update</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showAddModal"
            width="md:w-1/2 lg:w-1/4"
            @close="addModalClosed"
        >
            <template slot="header">Add Ring Group</template>
            <template slot="body">
                <div>
                    <app-input
                        v-model="addForm.name"
                        placeholder="Group Name"
                        :error="addForm.errors.get('name')"
                        @updated="addForm.errors.clear('name')"
                        focus
                        required
                    />
                    <app-input
                        v-model="addForm.ext"
                        placeholder="Extension"
                        :error="addForm.errors.get('ext')"
                        @updated="addForm.errors.clear('ext')"
                        required
                    />
                    <app-input
                        v-model="addForm.extensions"
                        placeholder="Destinations"
                        :error="addForm.errors.get('extensions')"
                        @updated="addForm.errors.clear('extensions')"
                        required
                        help-text="List of all ring group extensions separated by ,"
                    />
                    <app-input
                        v-model="addForm.max_limit"
                        placeholder="Incoming Limit (per call)"
                        :error="addForm.errors.get('max_limit')"
                        @updated="addForm.errors.clear('max_limit')"
                        :limit="2"
                        integer
                    />
                    <div class="mt-5 flex items-center justify-end">
                        <app-button 
                            class="btn-white" 
                            @click="showAddModal = false"
                            :disable="addFormIsLoading"
                        >Cancel</app-button>
                        <app-button 
                            class="btn-blue ml-3" 
                            @click="store"
                            :loading="addFormIsLoading"
                        >Add</app-button>
                    </div>
                </div>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import Form from '@/helpers/Form';
    export default {
        data() {
            return {
                addForm: new Form({
                    name: null,
                    ext: null,
                    extensions: null,
                    max_limit: 1,
                }),
                editForm: new Form({
                    name: null,
                    ext: null,
                    extensions: null,
                    max_limit: 1,
                }),
                editFormIsLoading: false,
                addFormIsLoading: false,
                tableIsLoading: false,
                refreshTable: false,
                showEditModal: false,
                showAddModal: false,
                extensions: [],
                q: null,
                generalInputIsLoading: false,
                columns: [
                    {
                        title: 'Group Name',
                        field: 'name',
                        sorter: 'string',
                    },
                    {
                        title: 'Extension',
                        field: 'ext',
                        sorter: 'string',
                    },
                    {
                        title: 'Destinations',
                        field: 'destinations',
                        sorter: 'string',
                    },
                ],
                actions: [
                    {
                        label: 'Edit',
                        icon: () => import('@/icons/EditOutlined.vue'),
                        requiredSelect: true,
                        handler: extensions => {
                            this.editForm.set(extensions[0]);
                            this.editForm.set({extensions: extensions[0].destinations});
                            this.showEditModal = true;
                        }
                    },
                    {
                        label: 'Delete',
                        icon: () => import('@/icons/DeleteOutlined.vue'),
                        requiredSelect: true,
                        handler: extensions => {
                            this.deleteItem(extensions[0]);
                        }
                    },
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: this.add,
                    }
                ],
            }
        },
        methods: {            
            editModalClosed() {
                this.editForm.reset();
                this.editForm.errors.clear();                
            },
            update() {
                this.editFormIsLoading = true;
                this.$store.dispatch('pbxware/ringGroups/update', this.editForm.payload())
                    .then(() => {
                        this.editFormIsLoading = false;
                        this.showEditModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Ring group was successfully updated.'
                        })
                    })
                    .catch(({ response }) => {
                        this.editFormIsLoading = false;
                        this.editForm.errors.record(response.data);

                        if (this.editForm.errors.has('error')) {
                            this.$swal.fire({
                                type: 'error',
                                title: 'Oops!',
                                text: this.editForm.errors.get('error')
                            });
                        } else {
                            this.$swal.fire({
                                type: 'error',
                                title: 'Oops!',
                                text: response.data.message
                            });
                        }
                    });
            },
            addModalClosed() {
                this.addForm.reset();
                this.addForm.errors.clear();
            },
            add() {
                this.showAddModal = true;
            },
            store() {
                this.addFormIsLoading = true;
                this.$store.dispatch('pbxware/ringGroups/store', this.addForm.payload())
                    .then(() => {
                        this.addFormIsLoading = false;
                        this.showAddModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Ring group was added successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.addFormIsLoading = false;
                        this.addForm.errors.record(response.data);
                    })
            },
            deleteItem(item) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to delete "<strong>${item.name}</strong>" ring group.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, delete it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('pbxware/ringGroups/delete', item.id);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Ring group was successfully deleted.'
                        })
                    }
                })
                
            },
            search() {

            }
        }
    }
</script>

<style lang="scss" scoped>
    .edit-form {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
        grid-gap: theme('spacing.5');
    }
</style>